import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useHistory } from 'react-router-dom';
import LocalOpsSheet from './LocalOpsSheet';
import PostMediaCarousel from '../post/PostMediaCarousel';
import { normalizeMediaList } from '../../utils/postMediaUtils';
import { getCoarseLocationLabel } from '../../utils/localOpsLocation';
import {
  getLocalOpsConfig,
  formatJGK,
  formatPercent,
} from '../../utils/localOpsConfig';
import axiosConn from '../../axiosConn';
import './localops.css';
import '../../styles/jgLocalOpsBadge.css';
import JGLocalOpsMark from '../common/JGLocalOpsMark';

const toLabel = (value) =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim() || 'Task';

const roundTo = (value, places = 2) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const factor = 10 ** places;
  return Math.round(parsed * factor) / factor;
};

const approximatelyEqual = (left, right, tolerance = 0.01) =>
  Number.isFinite(left) &&
  Number.isFinite(right) &&
  Math.abs(left - right) <= tolerance;

const resolveTaskPriceUsd = (task) => {
  if (!task) return null;
  const candidates = [
    task.price,
    task.priceUsd,
    task.price_usd,
    task.meta?.price,
    task.meta?.priceUsd,
  ];
  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return null;
};

const normalizeTaskTrustRequirement = (value) =>
  String(value || '').trim().toLowerCase() === 'verified_local'
    ? 'verified_local'
    : 'open';

const formatUsd = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '$0.00';
  return `$${roundTo(parsed, 2).toFixed(2)}`;
};

const redactLocationLabel = (label) => {
  if (!label) return '';
  const parts = String(label)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  const first = parts[0];
  const looksLikeStreet =
    /\d/.test(first) ||
    /\b(st|street|ave|avenue|rd|road|ln|lane|dr|drive|blvd|boulevard|hwy|highway|pkwy|parkway|ct|court|trl|trail|pl|place)\b/i.test(
      first,
    );
  if (looksLikeStreet && parts.length > 1) {
    return parts.slice(1).join(', ') || 'Nearby';
  }
  if (looksLikeStreet) return 'Nearby';
  return parts.join(', ');
};

const firstText = (...values) => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return '';
};

const getPrivatePickupAddress = (task) =>
  firstText(
    task?.originLocation?.address,
    task?.origin?.address,
    task?.pickupAddress,
    task?.meta?.pickupAddress,
  );

const getPrivateDeliveryAddress = (task) =>
  firstText(
    task?.meta?.deliveryAddress,
    task?.deliveryAddress,
    task?.destinationAddress,
    task?.meta?.destinationAddress,
    task?.destinationLocation?.address,
  );

const resolveDisplayTaskType = (task) => {
  const explicitType = firstText(
    task?.taskType,
    task?.category,
    task?.meta?.taskType,
  );
  if (explicitType) return explicitType;
  const transportType = firstText(task?.type);
  if (transportType && transportType !== 'task') return transportType;
  if (firstText(task?.meta?.deliveryAddress, task?.deliveryAddress)) {
    return 'delivery';
  }
  return transportType || 'task';
};

const buildPayoutSummary = (task, config, priceOverride) => {
  if (!task || !config) return null;
  const feePercent =
    Number.isFinite(task.platformFeePercent) && task.platformFeePercent > 0
      ? task.platformFeePercent
      : config.feePercent;
  const feeRate = feePercent / 100;
  const priceUsd =
    Number.isFinite(priceOverride) && priceOverride > 0
      ? priceOverride
      : resolveTaskPriceUsd(task);
  if (!Number.isFinite(priceUsd) || priceUsd <= 0) return null;
  const boostUsd = Number(task.platformBoostAmount);
  const boostAmountUsd =
    Number.isFinite(boostUsd) && boostUsd > 0 ? roundTo(boostUsd, 2) : 0;
  const baselineFeeUsd = Number(task.platformFeeAmountBeforeBoost);
  const settlementFeeUsd = Number(task.platformFeeAmount);
  const hasBaselineFee =
    boostAmountUsd > 0 && Number.isFinite(baselineFeeUsd) && baselineFeeUsd >= 0;
  const percentFeeUsd = roundTo(priceUsd * feeRate, 2);
  const hasSettlementFee =
    Number.isFinite(settlementFeeUsd) && settlementFeeUsd >= 0;
  const settlementFeeWithBoost = hasSettlementFee
    ? roundTo(settlementFeeUsd + boostAmountUsd, 2)
    : null;
  const settlementLooksBoostReduced =
    boostAmountUsd > 0 &&
    hasSettlementFee &&
    settlementFeeUsd < percentFeeUsd &&
    approximatelyEqual(settlementFeeWithBoost, percentFeeUsd);
  const taskFeeUsd = hasBaselineFee
    ? baselineFeeUsd
    : boostAmountUsd > 0 && settlementLooksBoostReduced
    ? settlementFeeWithBoost
    : hasSettlementFee
    ? settlementFeeUsd
    : percentFeeUsd;
  const feeUsd = roundTo(taskFeeUsd, 2);
  const taskNetUsd = Number(task.providerPayoutAmount);
  const computedNetUsd = roundTo(
    Math.max(0, priceUsd - feeUsd + boostAmountUsd),
    2,
  );
  const netUsd =
    boostAmountUsd > 0
      ? Number.isFinite(taskNetUsd) &&
        taskNetUsd > 0 &&
        approximatelyEqual(roundTo(taskNetUsd, 2), computedNetUsd)
        ? roundTo(taskNetUsd, 2)
        : computedNetUsd
      : Number.isFinite(taskNetUsd) && taskNetUsd > 0
      ? roundTo(taskNetUsd, 2)
      : computedNetUsd;
  const rate =
    Number.isFinite(config.jgkPerUsd) && config.jgkPerUsd > 0
      ? config.jgkPerUsd
      : 1000;
  return {
    totalJgkLabel: formatJGK(priceUsd * rate),
    totalUsdLabel: formatUsd(priceUsd),
    feePercentLabel: formatPercent(feePercent),
    feeJgkLabel: formatJGK(feeUsd * rate),
    feeUsdLabel: formatUsd(feeUsd),
    netJgkLabel: formatJGK(netUsd * rate),
    netUsdLabel: formatUsd(netUsd),
    boostJgkLabel: boostAmountUsd > 0 ? formatJGK(boostAmountUsd * rate) : null,
    boostUsdLabel: boostAmountUsd > 0 ? formatUsd(boostAmountUsd) : null,
    hasPlatformBoost: boostAmountUsd > 0,
  };
};

export default function LocalOpsTaskDetailSheet({
  open,
  task,
  onClose,
  onAccept,
  onStart,
  onComplete,
  onShare,
  onMessage,
  currentPlayerId,
  currentPlayerHandle,
  currentPlayerActionCreditCount = 0,
  verifiedLocalGateEnabled = false,
  hasOpenUnlockTask = false,
  onFindOpenTask = null,
  currentPlayerToken,
  onCancel,
  presenceEntry,
}) {
  const history = useHistory();
  const [startLocked, setStartLocked] = React.useState(false);
  const [capsule, setCapsule] = useState(null);
  const [capsuleError, setCapsuleError] = useState('');
  const [capsuleLoading, setCapsuleLoading] = useState(false);
  const mediaList = useMemo(
    () => (task ? normalizeMediaList(task) : []),
    [task],
  );
  const localOpsConfig = useMemo(() => getLocalOpsConfig(), []);
  const toNumber = (value) => {
    if (value === '' || value === null || value === undefined) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const rate =
    Number.isFinite(localOpsConfig?.jgkPerUsd) && localOpsConfig.jgkPerUsd > 0
      ? localOpsConfig.jgkPerUsd
      : 1000;
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[LocalOpsTaskDetailSheet][task-snapshot]', {
        id: task?._id || task?.id || task?.taskId,
        price: task?.price,
        priceUsd: task?.priceUsd,
        price_usd: task?.price_usd,
        providerPayoutAmount: task?.providerPayoutAmount,
        requiredJGK: task?.requiredJGK ?? task?.requiredJgk ?? task?.required_jgk,
        escrowAmountJGK: task?.escrowAmountJGK ?? task?.escrowAmount ?? task?.escrow_amount,
        rate,
        rawTask: task,
        keys: task ? Object.keys(task) : [],
      });
    }
  }, [task, rate]);
  const priceUsd = useMemo(() => {
    const base = toNumber(resolveTaskPriceUsd(task));
    if (base !== null && base > 0) return base;
    const requiredJGK =
      toNumber(task?.requiredJGK) ??
      toNumber(task?.requiredJgk) ??
      toNumber(task?.required_jgk) ??
      toNumber(task?.escrowAmountJGK) ??
      toNumber(task?.escrowAmount) ??
      toNumber(task?.escrow_amount) ??
      null;
    if (Number.isFinite(requiredJGK) && requiredJGK > 0) {
      return requiredJGK / rate;
    }
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[LocalOpsTaskDetailSheet][price-debug]', {
        priceCandidates: {
          price: task?.price,
          priceUsd: task?.priceUsd,
          price_usd: task?.price_usd,
          metaPrice: task?.meta?.price,
          metaPriceUsd: task?.meta?.priceUsd,
        },
        requiredJGK:
          task?.requiredJGK ??
          task?.requiredJgk ??
          task?.required_jgk ??
          task?.escrowAmountJGK ??
          task?.escrowAmount ??
          task?.escrow_amount ??
          null,
        rate,
        base,
      });
    }
    return base;
  }, [task, rate]);
  const payoutSummary = useMemo(
    () => buildPayoutSummary(task, localOpsConfig, priceUsd),
    [task, localOpsConfig, priceUsd],
  );
  const priceJgkRaw = useMemo(() => {
    const requiredJGK =
      toNumber(task?.requiredJGK) ??
      toNumber(task?.requiredJgk) ??
      toNumber(task?.required_jgk) ??
      toNumber(task?.escrowAmountJGK) ??
      toNumber(task?.escrowAmount) ??
      toNumber(task?.escrow_amount) ??
      null;
    if (Number.isFinite(requiredJGK) && requiredJGK > 0) return requiredJGK;
    if (Number.isFinite(priceUsd)) return priceUsd * rate;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('[LocalOpsTaskDetailSheet][jgk-debug]', {
        requiredJGK:
          task?.requiredJGK ??
          task?.requiredJgk ??
          task?.required_jgk ??
          task?.escrowAmountJGK ??
          task?.escrowAmount ??
          task?.escrow_amount ??
          null,
        priceUsd,
        rate,
      });
    }
    return null;
  }, [task, priceUsd, rate]);

  const priceDisplayUsd = useMemo(() => {
    if (Number.isFinite(priceUsd)) return priceUsd;
    if (Number.isFinite(priceJgkRaw)) return priceJgkRaw / rate;
    return null;
  }, [priceUsd, priceJgkRaw, rate]);

  const priceJgkLabel = useMemo(() => {
    if (!Number.isFinite(priceJgkRaw)) return null;
    return formatJGK(priceJgkRaw);
  }, [priceJgkRaw]);
  const taskType = resolveDisplayTaskType(task);
  const label = toLabel(taskType);
  const isDeliveryTask = taskType === 'delivery';
  const status = task?.status || 'discoverable';
  const ownerLabel =
    task?.creator?.playerId ||
    task?.playerId ||
    task?.ownerId ||
    task?.requesterId ||
    task?.createdBy ||
    'Requester';
  const ownerPhoto =
    presenceEntry?.profilePhoto ||
    task?.profilePhoto ||
    task?.creator?.profilePhoto ||
    task?.ownerPhoto ||
    task?.avatar ||
    null;
  const availability =
    task?.availabilityState ||
    task?.availability ||
    presenceEntry?.availabilityState ||
    presenceEntry?.status;
  const isPaymentReady =
    Number.isFinite(priceDisplayUsd) && !task?.quoteRequested;
  const isQuoteRequired = task?.quoteRequested === true;
  const isFunded = task?.escrowStatus === 'held';
  const isCompleted = status === 'completed';
  const locationLabel = getCoarseLocationLabel(task) || 'Nearby';
  const postAcceptStatuses = new Set(['accepted', 'in_progress']);
  const isCreator =
    (currentPlayerId &&
      (task?.creatorPlayerId === currentPlayerId ||
        task?.ownerId === currentPlayerId ||
        task?.playerId === currentPlayerId)) ||
    (currentPlayerHandle &&
      (task?.creatorPlayerId === currentPlayerHandle ||
        task?.ownerId === currentPlayerHandle ||
        task?.playerId === currentPlayerHandle));
  const isAccepted =
    (currentPlayerId &&
      (task?.acceptedPlayerId === currentPlayerId ||
        task?.providerId === currentPlayerId ||
        task?.acceptedPlayer?.playerId === currentPlayerId ||
        task?.provider?.playerId === currentPlayerId)) ||
    (currentPlayerHandle &&
      (task?.acceptedPlayerId === currentPlayerHandle ||
        task?.providerId === currentPlayerHandle ||
        task?.acceptedPlayer?.playerId === currentPlayerHandle ||
        task?.provider?.playerId === currentPlayerHandle));
  const viewerActionCreditTotal = Number(currentPlayerActionCreditCount || 0);
  const viewerIsVerifiedLocal =
    Number.isFinite(viewerActionCreditTotal) && viewerActionCreditTotal >= 1;
  const taskRequiresVerifiedLocal =
    normalizeTaskTrustRequirement(task?.trustRequirement) === 'verified_local';
  const verifiedLocalLocked =
    verifiedLocalGateEnabled &&
    taskRequiresVerifiedLocal &&
    !viewerIsVerifiedLocal &&
    !isCreator &&
    !isAccepted;
  const canShowAccept =
    status === 'discoverable' && !task?.acceptedPlayerId && !isCreator;
  const canAccept =
    canShowAccept && !verifiedLocalLocked;
  const canStart =
    isAccepted &&
    !['completed', 'cancelled', 'expired', 'started', 'in_progress'].includes(
      status,
    ) &&
    (status === 'accepted' ||
      (status === 'discoverable' && isAccepted)); /* fallback guard */
  const canComplete =
    isCreator &&
    (status === 'accepted' || status === 'in_progress' || status === 'started');
  const showPayout = !!payoutSummary;
  const canCancel =
    isCreator && !['completed', 'cancelled', 'expired'].includes(status);
  const canShareLive =
    task?.liveTrackingEnabled &&
    ['accepted', 'started', 'in_progress'].includes(status);
  const shouldShare =
    isCreator &&
    onShare &&
    !['completed', 'cancelled', 'expired'].includes(status);
  const showShareHelper = canShareLive || shouldShare;
  const shouldShowAv =
    task?.avEligible || task?.avContext || task?.allowAv || task?.avEnabled;
  const showPrivateAddresses =
    postAcceptStatuses.has(status) && (isAccepted || isCreator);
  const displayLocationLabel =
    showPrivateAddresses ? locationLabel : redactLocationLabel(locationLabel) || 'Nearby';
  const pickupAddress = getPrivatePickupAddress(task);
  const deliveryAddress = getPrivateDeliveryAddress(task);
  const acceptedPlayerIdRaw =
    task?.accepted?.playerId ||
    task?.acceptedPlayer?.playerId ||
    task?.provider?.playerId ||
    task?.acceptedPlayerHandle ||
    task?.providerHandle ||
    task?.acceptedPlayerId ||
    task?.providerId ||
    null;
  const acceptedPlayerId =
    acceptedPlayerIdRaw &&
    !/^[a-f0-9]{24}$/i.test(String(acceptedPlayerIdRaw || '').trim())
      ? acceptedPlayerIdRaw
      : null;
  const acceptedPlayerPhoto =
    task?.accepted?.profilePhoto ||
    task?.acceptedPlayer?.profilePhoto ||
    task?.provider?.profilePhoto ||
    task?.providerPhoto ||
    null;
  const statusLabel = (() => {
    if (['completed', 'cancelled', 'expired'].includes(status)) return status;
    if (status === 'in_progress' || status === 'started') return 'in_progress';
    if (status === 'accepted') return 'accepted';
    if (acceptedPlayerId || isAccepted) return 'accepted';
    return status || 'discoverable';
  })();
  const handleCashoutNow = useCallback(() => {
    history.push('/jgktoken#cashout');
  }, [history]);
  const handleViewWallet = useCallback(() => {
    history.push('/jgktoken');
  }, [history]);

  useEffect(() => {
    const taskId = task?._id || task?.taskId || task?.id;
    const acceptedId = task?.acceptedPlayerId || task?.acceptedPlayer?._id;
    if (!open || !taskId || !acceptedId || !isCreator || !currentPlayerToken) {
      setCapsule(null);
      setCapsuleError('');
      return;
    }
    let cancelled = false;
    const fetchCapsule = async () => {
      setCapsuleLoading(true);
      setCapsuleError('');
      try {
        const headers = { Authorization: `Bearer ${currentPlayerToken}` };
        const res = await axiosConn.get(
          `local-ops/tasks/${taskId}/assignee-capsule`,
          { headers },
        );
        if (cancelled) return;
        setCapsule(res?.data?.data || null);
      } catch (err) {
        if (cancelled) return;
        setCapsuleError('Identity signals unavailable.');
      } finally {
        if (!cancelled) setCapsuleLoading(false);
      }
    };
    fetchCapsule();
    return () => {
      cancelled = true;
    };
  }, [open, task, isCreator, currentPlayerToken]);

  useEffect(() => {
    setStartLocked(false);
  }, [task?._id, status]);

  if (!task) return null;

  const handleAvRequest = () => {
    if (typeof window === 'undefined') return;
    const id = task?.taskId || task?.id || task?._id;
    const providerId =
      task?.providerId || task?.assigneeId || task?.driverId || null;
    window.dispatchEvent(
      new CustomEvent('localops:av:request', {
        detail: { taskId: id, providerId },
      }),
    );
  };

  const actionCreditOutcomeGuaranteed =
    task?.actionCreditEligible !== false &&
    task?.actionCreditEligibleForViewer !== false &&
    task?.actionCredit?.eligible !== false &&
    task?.actionCredit?.duplicate !== true &&
    task?.verifiedRecord?.duplicate !== true;
  const viewerCanEarnTask =
    !isCreator && !verifiedLocalLocked && (canAccept || isAccepted);
  const taskPayoutLine = Number.isFinite(priceDisplayUsd)
    ? viewerCanEarnTask
      ? `Earn ${formatUsd(priceDisplayUsd)} from this task`
      : isCreator
      ? `Helper payout: ${formatUsd(priceDisplayUsd)} after verification`
      : `Payout: ${formatUsd(priceDisplayUsd)} after verification`
    : null;

  return (
    <LocalOpsSheet open={open} onClose={onClose} title="Task">
      <div className="localops-badge-inline">
        <JGLocalOpsMark />
      </div>
      <div className="localops-detail-hero">
        <div className="localops-detail-hero__top">
          <div className="localops-chip-group">
            <span className="localops-pill-chip">{label}</span>
            <span className="localops-pill-chip">{statusLabel}</span>
            {task?.sponsored || task?.isSponsored ? (
              <span className="localops-pill-chip">Sponsored</span>
            ) : null}
            {isCreator && acceptedPlayerId ? (
              <span className="localops-pill-chip localops-pill-chip--owner">
                Accepted by {acceptedPlayerId}
              </span>
            ) : null}
          </div>
          {availability ? (
            <div className="localops-presence-chip">
              <span
                className={`localops-presence-dot ${
                  availability === 'busy'
                    ? 'is-busy'
                    : availability === 'idle'
                    ? 'is-idle'
                    : availability === 'offline'
                    ? 'is-offline'
                    : ''
                }`}
              />
              {availability}
            </div>
          ) : null}
        </div>
        <div className="localops-chip-group localops-chip-group--trust">
          {viewerIsVerifiedLocal ? (
            <span className="localops-pill-chip localops-pill-chip--verified">
              Verified Local
            </span>
          ) : null}
          {isPaymentReady ? (
            <span className="localops-pill-chip localops-pill-chip--ready">
              Payment Ready
            </span>
          ) : isQuoteRequired ? (
            <span className="localops-pill-chip">Quote Required</span>
          ) : null}
          {isFunded ? (
            <span className="localops-pill-chip localops-pill-chip--funded">
              Funded
            </span>
          ) : null}
        </div>
        <div className="localops-owner-row">
          <span className="localops-avatar">
            {ownerPhoto ? (
              <img src={ownerPhoto} alt="" />
            ) : (
              <span className="localops-avatar__fallback">
                {String(ownerLabel || '?').charAt(0).toUpperCase()}
              </span>
            )}
            {availability ? (
              <span
                className={`localops-presence-dot ${
                  availability === 'busy'
                    ? 'is-busy'
                    : availability === 'idle'
                    ? 'is-idle'
                    : availability === 'offline'
                    ? 'is-offline'
                    : ''
                }`}
              />
            ) : null}
          </span>
          <div>
            <div className="localops-owner-title">
              {ownerLabel}
              {isCreator ? ' (You)' : ''}
            </div>
            <div className="localops-helper">Local requester</div>
          </div>
          {isCreator && acceptedPlayerId ? (
            <div className="localops-accepted-by">
              {acceptedPlayerPhoto ? (
                <img
                  src={acceptedPlayerPhoto}
                  alt={acceptedPlayerId}
                  className="localops-accepted-by__avatar"
                />
              ) : (
                <span className="localops-accepted-by__avatar localops-avatar__fallback">
                  {(acceptedPlayerId || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              <div className="localops-accepted-by__meta">
                <div className="localops-helper">Accepted by</div>
          <div className="localops-owner-title">{acceptedPlayerId}</div>
        </div>
      </div>
    ) : null}

      {isCreator && acceptedPlayerId ? (
        <div className="localops-capsule-panel">
          <div className="localops-capsule-header">
            Task Helper (task-scoped)
          </div>
          {capsuleLoading ? (
            <div className="localops-capsule-text">Loading identity…</div>
          ) : capsuleError ? (
            <div className="localops-capsule-text localops-capsule-text--muted">
              {capsuleError}
            </div>
          ) : capsule ? (
            <>
              <div className="localops-capsule-title">
                {capsule.capsule?.displayName || 'LocalOps contributor'}
              </div>
              <div className="localops-capsule-text">
                {capsule.capsule?.trustSignals?.completedCount || 0} completions •{' '}
                {capsule.capsule?.trustSignals?.payoutVerified
                  ? 'payout verified'
                  : 'payout pending'}
              </div>
              <div className="localops-capsule-hint">
                Task-scoped identity. Verified by LocalOps activity — not public profile.
              </div>
            </>
          ) : null}
        </div>
      ) : null}
        </div>
        <h3 className="localops-detail-title">
          {task?.title || task?.name || task?.meta?.title || 'Task'}
        </h3>
        <div className="localops-detail-sub">{displayLocationLabel}</div>
        <div className="localops-detail-sub">
          {isDeliveryTask
            ? 'For safety, pickup and delivery addresses are shared only after a helper accepts.'
            : 'For safety, the pickup address is shared only after a helper accepts.'}
        </div>
        {Number.isFinite(priceDisplayUsd) ? (
          <div className="localops-price-row" style={{ marginTop: 12, marginBottom: 8 }}>
            <div className="localops-detail-sub" style={{ fontWeight: 700 }}>
              {taskPayoutLine}
            </div>
            {priceJgkLabel ? (
              <div className="localops-helper">
                Funded: {priceJgkLabel} held until completion is verified.
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {showPrivateAddresses && (pickupAddress || deliveryAddress) ? (
        <div className="localops-section">
          {pickupAddress ? (
            <div className="localops-helper">
              <strong>Pickup address (private):</strong> {pickupAddress}
            </div>
          ) : null}
          {deliveryAddress ? (
            <div className="localops-helper">
              <strong>Delivery address (private):</strong> {deliveryAddress}
            </div>
          ) : null}
        </div>
      ) : null}
      {task?.summary || task?.description || task?.meta?.description ? (
        <p>{task?.summary || task?.description || task?.meta?.description}</p>
      ) : null}
      {showPayout ? (
        <div className="localops-section">
          {isCompleted ? (
            <div className="localops-success-card">
              <div className="localops-success-title">
                {'Task completed \uD83C\uDF89'}
              </div>
              <div className="localops-success-subtitle">
                Your JGK Balance has been updated.
              </div>
            </div>
          ) : (
            <h4>Payout after verification</h4>
          )}
          {isCompleted ? (
            <>
              <div className="localops-helper">
                That was real local value in motion.
              </div>
              <div className="localops-helper">
                Completed actions keep communities moving.
              </div>
            </>
          ) : null}
          <div className="localops-pay-card">
            <div className="localops-pay-row">
              <span>Task total</span>
              <span className="localops-pay-amount">
                {payoutSummary.totalJgkLabel}
                <span className="localops-pay-sub">
                  ({payoutSummary.totalUsdLabel})
                </span>
              </span>
            </div>
            <div className="localops-pay-row">
              <span>Platform fee ({payoutSummary.feePercentLabel})</span>
              <span className="localops-pay-amount">
                {payoutSummary.feeJgkLabel}
                <span className="localops-pay-sub">
                  ({payoutSummary.feeUsdLabel})
                </span>
              </span>
            </div>
            {payoutSummary.hasPlatformBoost ? (
              <div className="localops-pay-row">
                <span>Local activity boost</span>
                <span className="localops-pay-amount">
                  +{payoutSummary.boostJgkLabel}
                  <span className="localops-pay-sub">
                    (+{payoutSummary.boostUsdLabel})
                  </span>
                </span>
              </div>
            ) : null}
            <div className="localops-pay-row localops-pay-row--emphasis">
              <span>{isCreator ? 'Helper receives' : 'You receive'}</span>
              <span className="localops-pay-amount">
                {payoutSummary.netJgkLabel}
                <span className="localops-pay-sub">
                  ({payoutSummary.netUsdLabel})
                </span>
              </span>
            </div>
          </div>
          <div className="localops-helper">
            {payoutSummary.hasPlatformBoost
              ? 'Platform support is boosting this task payout.'
              : 'We take a small platform fee to keep LocalOps running.'}
          </div>
          {isCompleted ? (
            <>
              <div className="localops-earn-row">
                <CheckCircleOutlineIcon
                  className="localops-earn-icon"
                  fontSize="small"
                />
                <span>You earned {payoutSummary.netJgkLabel}.</span>
              </div>
              <div className="localops-getpaid">
                <div className="localops-getpaid__title">Get paid</div>
                <div className="localops-helper">
                  You can use your JGK Balance or cash out anytime.
                </div>
                <div className="localops-success-actions">
                  <button
                    type="button"
                    className="localops-btn"
                    onClick={handleCashoutNow}
                  >
                    Cash out now
                  </button>
                  <button
                    type="button"
                    className="localops-btn localops-btn--ghost"
                    onClick={handleViewWallet}
                  >
                    View wallet
                  </button>
                </div>
                <div className="localops-getpaid__meta localops-helper">
                  No minimum balance required. No hidden fees. You're always in
                  control of your balance.
                </div>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      {task?.etaMinutes ? (
        <div className="localops-helper">ETA: {task.etaMinutes} min</div>
      ) : null}
      {task?.liveTrackingEnabled &&
      ['accepted', 'started', 'in_progress'].includes(status) ? (
        <>
          <div className="localops-helper">
            Live route is visible on the map.
          </div>
          <div className="localops-live-card">
            <div className="localops-chip-row">
              <span className="localops-pill-chip">{label}</span>
              <span className="localops-pill-chip localops-pill-chip--ready">
                {statusLabel}
              </span>
            </div>
            <div className="localops-live-title">Live tracking enabled</div>
            <div className="localops-helper">
              Your route stays private to the task owner while you’re en route.
              Use Share route to send a link if you need to loop in a trusted
              helper.
            </div>
            <div className="localops-live-actions">
              <button
                type="button"
                className="localops-btn localops-btn--ghost"
                onClick={() => onShare?.(task)}
              >
                Share route
              </button>
            </div>
          </div>
        </>
      ) : null}
      {mediaList.length ? (
        <div className="localops-media-hero">
          <PostMediaCarousel item={task} autoPlay={false} enableZoom />
        </div>
      ) : null}
      {verifiedLocalLocked ? (
        <div className="localops-section">
          <div className="localops-helper" style={{ fontWeight: 700 }}>
            Verified Local required
          </div>
          <div className="localops-helper">
            This task is for Verified Local helpers. Verified Local starts at
            Action Credit 1, earned from verified local completions.
          </div>
          <div className="localops-helper">
            Your Action Credit: {Math.max(0, viewerActionCreditTotal)} / 1
          </div>
          {hasOpenUnlockTask && typeof onFindOpenTask === 'function' ? (
            <div className="localops-actions">
              <button
                type="button"
                className="localops-btn"
                onClick={onFindOpenTask}
              >
                Find open task
              </button>
              <button
                type="button"
                className="localops-btn localops-btn--ghost"
                onClick={onClose}
              >
                Back to map
              </button>
            </div>
          ) : (
            <>
              <div className="localops-helper">
                No open tasks nearby right now.
              </div>
              <div className="localops-helper">
                Check back soon or return to the map.
              </div>
              <div className="localops-actions">
                <button
                  type="button"
                  className="localops-btn"
                  onClick={onClose}
                >
                  Back to map
                </button>
              </div>
            </>
          )}
        </div>
      ) : null}
      {canShowAccept && !verifiedLocalLocked ? (
        <div className="localops-section">
          <div className="localops-helper">
            Accept when you're ready to complete the work. The requester
            verifies completion, then your payout is released to your JGK
            Balance.
            {actionCreditOutcomeGuaranteed
              ? ' Verified completion strengthens your Action Credit record.'
              : ''}
          </div>
        </div>
      ) : null}
      {canComplete ? (
        <div className="localops-section">
          <div className="localops-helper">
            {actionCreditOutcomeGuaranteed
              ? "Confirm the work is complete. This releases the helper payout and strengthens the helper's Action Credit record."
              : 'Confirm the work is complete. This verifies the work and releases the helper payout.'}
          </div>
        </div>
      ) : null}
      <div className="localops-actions">
        {canShowAccept && !verifiedLocalLocked ? (
          <button
            type="button"
            className="localops-btn"
            onClick={() => {
              if (!canAccept) return;
              onAccept?.(task);
            }}
          >
            Accept task
          </button>
        ) : null}
        {canStart ? (
          <button
            type="button"
            className="localops-btn"
            onClick={() => {
              if (startLocked) return;
              setStartLocked(true);
              onStart?.(task);
            }}
            disabled={startLocked}
          >
            Start
          </button>
        ) : null}
        {canComplete ? (
          <button
            type="button"
            className="localops-btn"
            onClick={() => onComplete?.(task)}
          >
            Verify completion
          </button>
        ) : null}
        {canShareLive && !['completed', 'cancelled', 'expired'].includes(status) ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => onShare?.(task)}
          >
            Share live route
          </button>
        ) : null}
        {shouldShowAv ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={handleAvRequest}
          >
            Start A/V
          </button>
        ) : null}
        {shouldShare ? (
          <button
            type="button"
            className="localops-btn localops-btn--cta"
            onClick={() => onShare?.(task)}
          >
            Share & Earn JGK
          </button>
        ) : null}
        {!isCreator && isAccepted && onMessage ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => onMessage(task)}
          >
            Message owner
          </button>
        ) : null}
        {canCancel ? (
          <button
            type="button"
            className="localops-btn localops-btn--danger"
            onClick={() => {
              if (typeof window !== 'undefined') {
                const confirmed = window.confirm(
                  'Remove this task from the map?',
                );
                if (!confirmed) return;
              }
              onCancel?.(task);
            }}
          >
            Remove task
          </button>
        ) : null}
        {showShareHelper ? (
          <div className="localops-earn-row">
            <CheckCircleOutlineIcon
              className="localops-earn-icon"
              fontSize="small"
            />
            <span>Nice! Earn JGK Balance for sharing LocalOps.</span>
          </div>
        ) : null}
      </div>
    </LocalOpsSheet>
  );
}
